import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, ArrowUp, ArrowDown } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";

const EMPTY = { name: "", position: "", bio: "", image_path: null, sort_order: 0, email: "" };

export default function MembersManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/members").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ ...EMPTY, sort_order: items.length }); };
  const openEdit = (m) => {
    setEditing(m.id);
    setForm({
      name: m.name || "", position: m.position || "", bio: m.bio || "",
      image_path: m.image_path || null, sort_order: m.sort_order || 0, email: m.email || "",
    });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/members", form);
        toast.success(t("admin.members.toastCreated"));
      } else {
        await api.put(`/members/${editing}`, form);
        toast.success(t("admin.members.toastUpdated"));
      }
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/members/${id}`);
      toast.success(t("admin.members.toastDeleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveSort = async (m, dir) => {
    const sorted = [...items];
    const idx = sorted.findIndex((x) => x.id === m.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    try {
      await Promise.all([
        api.put(`/members/${m.id}`, { sort_order: other.sort_order }),
        api.put(`/members/${other.id}`, { sort_order: m.sort_order }),
      ]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="members-manager" className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.members.eyebrow")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("admin.members.title")}</h1>
        </div>
        <Button onClick={openNew} data-testid="new-member-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
          <Plus size={16} className="mr-2" /> {t("admin.members.newBtn")}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            <Users className="mx-auto mb-3" size={22} />
            <p className="text-sm">{t("admin.members.empty")}</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-beige-light border-b border-slate-200">
                <th className="px-4 py-3 font-medium text-slate-900">{t("admin.members.colOrder")}</th>
                <th className="px-4 py-3 font-medium text-slate-900">{t("admin.members.colMember")}</th>
                <th className="px-4 py-3 font-medium text-slate-900">{t("admin.members.colPosition")}</th>
                <th className="px-4 py-3 font-medium text-slate-900 text-right">{t("admin.common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m, i) => (
                <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-slate-100 hover:bg-beige-light/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveSort(m, "up")} disabled={i === 0} className="p-1.5 hover:bg-slate-100 rounded-sm text-slate-600 disabled:opacity-30" data-testid={`member-up-${m.id}`}>
                        <ArrowUp size={13} />
                      </button>
                      <button onClick={() => moveSort(m, "down")} disabled={i === items.length - 1} className="p-1.5 hover:bg-slate-100 rounded-sm text-slate-600 disabled:opacity-30" data-testid={`member-down-${m.id}`}>
                        <ArrowDown size={13} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {m.image_path ? (
                        <img src={fileUrl(m.image_path)} alt="" className="w-10 h-10 object-cover rounded-sm border border-slate-200" />
                      ) : (
                        <div className="w-10 h-10 bg-beige rounded-sm border border-slate-200 grid place-items-center text-slate-400">
                          <Users size={14} />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">{m.name}</div>
                        {m.email && <div className="text-xs text-slate-500">{m.email}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{m.position}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(m)} data-testid={`member-edit-${m.id}`} className="p-2 hover:bg-slate-100 rounded-sm text-slate-600 hover:text-bordeaux">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => setConfirmId(m.id)} data-testid={`member-delete-${m.id}`} className="p-2 hover:bg-red-50 rounded-sm text-slate-600 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-xl rounded-sm" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("admin.members.dialogNew") : t("admin.members.dialogEdit")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={t("admin.members.fieldName")}>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" className="rounded-sm border-slate-300" />
            </Field>
            <Field label={t("admin.members.fieldPosition")}>
              <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="member-position-input" className="rounded-sm border-slate-300" />
            </Field>
            <Field label={t("admin.members.fieldEmail")}>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email-input" className="rounded-sm border-slate-300" />
            </Field>
            <Field label={t("admin.members.fieldBio")}>
              <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="member-bio-input" className="rounded-sm border-slate-300" />
            </Field>
            <Field label={t("admin.members.fieldImage")}>
              <ImageUploader value={form.image_path} onChange={(p) => setForm({ ...form, image_path: p })} testId="member-image-uploader" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} className="rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900">{t("admin.common.cancel")}</Button>
            <Button onClick={save} disabled={busy} data-testid="member-save-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
              {busy ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.common.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.common.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-delete-cancel">{t("admin.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="member-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("admin.common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
